import './useStudentCoursesSavedOnlyIdHookConfig';
import { useReactiveVar } from '@apollo/client';
import { createElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { coursesSavedOnlyIdVar } from '../../cache/learner/coursesSavedOnlyIdReactiveVar';
import CheckCircleOutlinedIcon from '../../components/icons/svg/CheckCircleOutlinedIcon';
import ThinTrashIcon from '../../components/icons/svg/ThinTrashIcon';
import { NotificationContext } from '../../contexts/NotificationsContext';
import { StudentDataContext } from '../../contexts/StudentDataContext/StudentDataContext';
import { Mixpanel } from '../../mixpanel/mixpanel';
import { MixpanelComponentsNames } from '../../mixpanel/MixpanelEvents/mixpanelComponentNames';
import { MixpanelEventsEnum } from '../../mixpanel/MixpanelEvents/mixpanelEventsInterface';
import { useMutationDeleteSavedCourseByStudentId } from '../../operations/mutations/student/useDeleteSavedCoursesByStudentId/useDeleteSavedCoursesByStudentId';
import {
  IUpsertCollectionCourseParams,
  useMutationUpsertCollectionCourse,
} from '../../operations/mutations/student/useUpsertStudentCollectionCourse/useUpsertStudentCollectionCourse';
import { IStudentCourseSavedOnlyId } from '../../operations/query/students/useGetStudentCoursesSavedOnlyCourseId/useGetStudentCoursesSavedOnlyCourseId';
import { PATH_DASHBOARD_STUDENT } from '../../routes/paths';
import { COLORS } from '../../theme/colors';
import { decryptor, encryptor } from '../../utils/decryptor';
import { getMixpanelName } from '../../utils/getMixpanelName';
import useNotificationsHook from '../notificationsHook/useNotificationsHook';

const UNDO_TIMEOUT_MS = 10000;

export interface IStudentCoursesSavedOnlyIdHook {
  operations: {
    handleSetCoursesSavedOnlyId: (courses: IStudentCourseSavedOnlyId[]) => void;
    handleIsCourseSavedOnlyId: (courseId: string) => boolean;
    handleUpsertCourseSavedAndOnlyId: (params: IUpsertCollectionCourseParams, courseName?: string) => Promise<void>;
    setFromLocalCoursesSavedOnlyIdData: () => boolean;
    handleDeleteSavedCourse: (courseId: string, providerId: string, courseName?: string) => Promise<void>;
  };
}

const useStudentCoursesSavedOnlyIdHook = (): IStudentCoursesSavedOnlyIdHook => {
  const { t } = useTranslation('useStudentCoursesSavedOnlyIdHook');
  const { operations: notificationOps } = useNotificationsHook();
  const [upsertCollectionCourse] = useMutationUpsertCollectionCourse();
  const { refetchCoursesSavedOnlyId } = useContext(StudentDataContext);
  const { deleteNotification } = useContext(NotificationContext);
  const coursesDataReactiveVar = useReactiveVar(coursesSavedOnlyIdVar);
  const [deleteCourse] = useMutationDeleteSavedCourseByStudentId();

  const handleSetCoursesSavedOnlyId = (courses: IStudentCourseSavedOnlyId[]): void => {
    coursesSavedOnlyIdVar({
      data: courses,
      error: undefined,
    });

    const encryptedData = encryptor(JSON.stringify(courses));

    if (!encryptedData) {
      return;
    }

    sessionStorage.setItem('coursesSavedOnlyIdData', encryptedData);
  };

  const handleIsCourseSavedOnlyId = (courseId: string): boolean => {
    const coursesSaved = coursesSavedOnlyIdVar().data;

    return coursesSaved.some((course) => course.courseId === courseId);
  };

  const trackToastCtaCoursesTapped = (courseName?: string): void => {
    Mixpanel.track(
      {
        action: MixpanelEventsEnum.TOAST_CTA_COURSES_TAPPED,
        data: {
          screen: getMixpanelName(window.location.pathname.concat(window.location.search)),
          component: MixpanelComponentsNames.NOTIFICATION_COMPONENT,
          courseName: courseName ?? '',
        },
      },
      true,
    );
  };

  const handleUpsertCourseSavedAndOnlyId = (
    params: IUpsertCollectionCourseParams,
    courseName?: string,
  ): Promise<void> => {
    const idFound = coursesDataReactiveVar.data.some((course) => course.courseId === params.courseId);

    if (!idFound) {
      const updatedCourseIdData = coursesDataReactiveVar.data.concat({ courseId: params.courseId });

      coursesSavedOnlyIdVar({
        data: updatedCourseIdData,
        error: undefined,
      });
    }

    const isDefaultCollection = params.collectionId === 'default' || !params.collectionId;

    return upsertCollectionCourse({ variables: params })
      .then(() => {
        notificationOps.addNotification('success', t('UPSERT_DESCRIPTION_TEXT'), t('UPSERT_TITLE_TEXT'), {
          leftIcon: createElement(CheckCircleOutlinedIcon),
          linkText: isDefaultCollection ? t('VIEW_ALL_SAVED_COURSES_LINK_TEXT') : t('VIEW_COURSES_LINK_TEXT'),
          linkTo: isDefaultCollection
            ? PATH_DASHBOARD_STUDENT.admin.courses
            : `${PATH_DASHBOARD_STUDENT.admin.courses}?activeTab=1`,
          onLinkClick: () => trackToastCtaCoursesTapped(courseName),
        });
      })
      .catch(() => {
        notificationOps.addNotification('info', t('ERROR_UPSERT_DESCRIPTION_TEXT'), t('ERROR_UPSERT_TITLE_TEXT'), {
          variant: 'error',
        });
      })
      .finally(() => {
        refetchCoursesSavedOnlyId?.();
      });
  };

  const setFromLocalCoursesSavedOnlyIdData = (): boolean => {
    const coursesSavedOnlyIdData = sessionStorage.getItem('coursesSavedOnlyIdData');

    if (!coursesSavedOnlyIdData) {
      return false;
    }

    const decryptedData = JSON.parse(decryptor(coursesSavedOnlyIdData) as string);

    handleSetCoursesSavedOnlyId(decryptedData);

    return true;
  };

  const handleDeleteSavedCourse = async (courseId: string, providerId: string, courseName?: string): Promise<void> => {
    const updatedCourses = coursesDataReactiveVar.data.filter((course) => course.courseId !== courseId);

    handleSetCoursesSavedOnlyId(updatedCourses);

    let mutationExecuted = false;
    let undoTimerRef: ReturnType<typeof setTimeout> | null = null;

    const executeDelete = async (): Promise<void> => {
      mutationExecuted = true;
      await deleteCourse({ variables: { courseId, providerId } }).catch(() => {
        notificationOps.addNotification('danger', t('ERROR_DESCRIPTION_TEXT'), t('ERROR_TITLE_TEXT'));
        handleSetCoursesSavedOnlyId([...updatedCourses, { courseId }]);
      });
    };

    const handleUndo = async (): Promise<void> => {
      deleteNotification(notificationId);

      if (undoTimerRef) {
        clearTimeout(undoTimerRef);
        undoTimerRef = null;
      }

      if (mutationExecuted) {
        await upsertCollectionCourse({ variables: { courseId, providerId } })
          .then(() => {
            handleSetCoursesSavedOnlyId([...updatedCourses, { courseId }]);
            notificationOps.addNotification('success', t('UPSERT_DESCRIPTION_TEXT'), t('UPSERT_TITLE_TEXT'), {
              leftIcon: createElement(CheckCircleOutlinedIcon),
              linkText: t('VIEW_COURSES_LINK_TEXT'),
              linkTo: `${PATH_DASHBOARD_STUDENT.admin.courses}?activeTab=1`,
              onLinkClick: () => trackToastCtaCoursesTapped(courseName),
            });
          })
          .catch(() => {
            notificationOps.addNotification('danger', t('ERROR_DESCRIPTION_TEXT'), t('ERROR_TITLE_TEXT'));
          });
      } else {
        handleSetCoursesSavedOnlyId([...updatedCourses, { courseId }]);
      }
    };

    const notificationId = notificationOps.addNotification(
      'removed',
      t('DELETE_DESCRIPTION_TEXT'),
      t('DELETE_TITLE_TEXT'),
      {
        variant: 'error',
        leftIcon: createElement(ThinTrashIcon, { color: COLORS.iconMenuEditCollection }),
        linkText: t('UNDO_ACTION_TEXT'),
        onLinkClick: handleUndo,
      },
    );

    undoTimerRef = setTimeout(executeDelete, UNDO_TIMEOUT_MS);
  };

  return {
    operations: {
      handleSetCoursesSavedOnlyId,
      handleIsCourseSavedOnlyId,
      handleUpsertCourseSavedAndOnlyId,
      setFromLocalCoursesSavedOnlyIdData,
      handleDeleteSavedCourse,
    },
  };
};

export default useStudentCoursesSavedOnlyIdHook;
